import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  TextArea,
  DataTable,
  Skeleton,
  DatePicker,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import {
  useDeals,
  useCreateDeal,
  useActiveProducts,
  ChannelPill,
  StatusBadge,
} from '@/features/communications';
import type { Deal, DealChannel, ProductOption } from '@/features/communications';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';

const DEAL_CHANNEL_LIST: DealChannel[] = ['WHATSAPP', 'INSTAGRAM', 'FACEBOOK'];

type Row = Deal;

const dealColumns: ColumnDef<Row>[] = [
  {
    key: 'custom_text',
    header: 'Deal',
    render: (_v, row) => (
      <div>
        <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 line-clamp-1">
          {row.custom_text || `${row.product_ids.length} product deal`}
        </p>
        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
          {row.product_ids.length} product{row.product_ids.length > 1 ? 's' : ''}
        </p>
      </div>
    ),
  },
  {
    key: 'channels',
    header: 'Channels',
    render: (_v, row) => (
      <div className="flex flex-wrap gap-1">
        {row.channels.map((ch) => (
          <ChannelPill key={ch} channel={ch} />
        ))}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (_v, row) => <StatusBadge status={row.status} variant="log" />,
  },
  {
    key: 'created_at',
    header: 'Date',
    render: (_v, row) => (
      <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
        {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
      </span>
    ),
  },
];

// Currency-aware money formatter. Symbol and subunit factor come from the
// shared single-source helpers; the no-decimal `en-IN` grouping is preserved
// verbatim so the INR path stays byte-identical to the previous inline impl.
function fmtPaise(p: number, currency: CurrencyCode): string {
  const major = p / getSubunitMultiplier(currency);
  return `${getCurrencySymbol(currency)}${major.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

function DealOfDayContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Products picker
  const { data: products = [], isLoading: productsLoading } = useActiveProducts();
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [customText, setCustomText] = useState('');
  const [selectedChannels, setSelectedChannels] = useState<DealChannel[]>(['WHATSAPP']);
  const [dealDate, setDealDate] = useState<Date | undefined>(undefined);

  const createMutation = useCreateDeal();
  const dealPage = Number(searchParams.get('page') ?? '1');
  const { data: dealsData, isLoading: dealsLoading, isFetching } = useDeals(dealPage);
  const dealRows = useMemo<Row[]>(() => dealsData?.data ?? [], [dealsData]);

  // Product select options
  const productOptions = useMemo(
    () => [
      { value: '', label: '— Select a product —' },
      ...products.map((p: ProductOption) => ({
        value: p.id,
        label: `${p.name}${p.selling_price ? ` (${fmtPaise(p.selling_price, currency)})` : ''}`,
      })),
    ],
    [products, currency],
  );

  // Publish is valid only with ≥1 product and ≥1 channel. Drives both the
  // button's disabled state and the Ctrl+Enter shortcut; the ref keeps the
  // once-registered keydown handler in sync without re-binding.
  const canPublish = selectedProductIds.length > 0 && selectedChannels.length > 0;
  const canPublishRef = useRef(false);
  canPublishRef.current = canPublish;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canPublishRef.current) {
        e.preventDefault();
        handlePublish();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  function addProduct(productId: string) {
    if (!productId || selectedProductIds.includes(productId)) return;
    if (selectedProductIds.length >= 5) {
      toast({ variant: 'warning', title: 'Maximum 5 products per deal' });
      return;
    }
    setSelectedProductIds((prev) => [...prev, productId]);
  }

  function removeProduct(productId: string) {
    setSelectedProductIds((prev) => prev.filter((id) => id !== productId));
  }

  function toggleChannel(ch: DealChannel) {
    setSelectedChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  }

  const handlePublish = useCallback(() => {
    if (selectedProductIds.length === 0) {
      toast({ variant: 'warning', title: 'Add products', description: 'Select at least one product.' });
      return;
    }
    if (selectedChannels.length === 0) {
      toast({ variant: 'warning', title: 'Select channels', description: 'Choose at least one channel.' });
      return;
    }

    createMutation.mutate(
      {
        product_ids: selectedProductIds,
        custom_text: customText.trim() || undefined,
        channels: selectedChannels,
      },
      {
        onSuccess: () => {
          toast({ variant: 'success', title: 'Deal queued', description: 'The flyer is being made. Communication Logs show each customer it reaches.' });
          setSelectedProductIds([]);
          setCustomText('');
          setDealDate(undefined);
        },
        onError: (err) => {
          toast({ variant: 'error', title: 'Failed', description: (err as Error).message });
        },
      },
    );
  }, [selectedProductIds, customText, selectedChannels, createMutation, toast]);

  const selectedProducts = useMemo(
    () => products.filter((p: ProductOption) => selectedProductIds.includes(p.id)),
    [products, selectedProductIds],
  );

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(245,158,11,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Promotional
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Deal of the Day</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Select products, write a promo message, and publish to WhatsApp, Instagram, and Facebook.
          </p>
        </div>
      </div>

      {/* Form + Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Form — 2/3 */}
        <div className="lg:col-span-2 rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm shadow-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-5 border-l-4 border-amber-500 pl-3">
            Configure Deal
          </h2>

          <div className="space-y-5">
            {/* Product Picker */}
            <div>
              <Select
                label="Add Product"
                options={productOptions}
                value=""
                onChange={(v) => addProduct(typeof v === 'string' ? v : '')}
                disabled={productsLoading}
              />
              <p className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400">
                Select up to 5 products. {selectedProductIds.length}/5 selected.
              </p>
            </div>

            {/* Selected products list */}
            {selectedProducts.length > 0 ? (
              <div className="space-y-2">
                {selectedProducts.map((product: ProductOption, idx: number) => (
                  <div
                    key={product.id}
                    className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/10 dark:to-orange-900/10 p-3 shadow-sm"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {idx + 1}
                    </div>
                    {product.image_url ? (
                      <img src={product.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                    ) : null}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">{product.name}</p>
                      {product.selling_price ? (
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono tabular-nums">{fmtPaise(product.selling_price, currency)}</p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => removeProduct(product.id)}
                      className="rounded-lg p-1 text-neutral-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors shrink-0"
                      aria-label={`Remove ${product.name}`}
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            <TextArea
              label="Promotional Text"
              value={customText}
              onChange={setCustomText}
              placeholder="Today only — 50% off on premium range!"
              maxLength={200}
              rows={3}
            />

            <DatePicker
              label="Deal Date"
              value={dealDate}
              onChange={setDealDate}
              placeholder="Today"
              minDate={new Date()}
            />

            <div>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Channels</p>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Select distribution channels">
                {DEAL_CHANNEL_LIST.map((ch) => (
                  <button
                    key={ch}
                    type="button"
                    role="switch"
                    aria-pressed={selectedChannels.includes(ch)}
                    aria-label={ch === 'WHATSAPP' ? 'WhatsApp' : ch === 'INSTAGRAM' ? 'Instagram' : 'Facebook'}
                    onClick={() => toggleChannel(ch)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                      selectedChannels.includes(ch)
                        ? 'bg-amber-500 text-white shadow-sm'
                        : 'bg-neutral-100 dark:bg-neutral-700/60 text-neutral-700 dark:text-neutral-200 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                    }`}
                  >
                    {ch === 'WHATSAPP' ? 'WhatsApp' : ch === 'INSTAGRAM' ? 'Instagram' : 'Facebook'}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button
                variant="primary"
                onClick={handlePublish}
                loading={createMutation.isPending}
                disabled={!canPublish}
              >
                Publish Deal
              </Button>
            </div>
          </div>
        </div>

        {/* Preview — 1/3 */}
        <div className="rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm shadow-lg p-6 sticky top-6 self-start">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-4 border-l-4 border-amber-500 pl-3">
            Deal Preview
          </h2>

          <div className="relative rounded-xl overflow-hidden bg-gradient-to-br from-orange-400 via-amber-500 to-yellow-400 p-5 text-white">
            {/* Ribbon */}
            <div className="absolute top-3 -right-8 rotate-45 bg-red-600 text-white text-[9px] font-bold uppercase tracking-wider px-8 py-0.5 shadow-md">
              Today&apos;s Deal
            </div>

            <div className="relative mt-4">
              <p className="text-lg font-bold leading-tight">
                {customText || 'Your promotional text here...'}
              </p>

              <div className="mt-4 space-y-2">
                {selectedProducts.length > 0
                  ? selectedProducts.map((product: ProductOption, i: number) => (
                      <div
                        key={product.id}
                        className="flex items-center gap-2 rounded-lg bg-white/20 backdrop-blur-sm px-3 py-2"
                      >
                        <div className="h-8 w-8 rounded bg-white/30 flex items-center justify-center text-[10px] font-bold">
                          {i + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold truncate">{product.name}</p>
                          {product.selling_price ? (
                            <p className="text-[10px] font-mono tabular-nums opacity-80">{fmtPaise(product.selling_price, currency)}</p>
                          ) : null}
                        </div>
                      </div>
                    ))
                  : (
                    <div className="rounded-lg bg-white/20 backdrop-blur-sm px-3 py-4 text-center text-xs opacity-75">
                      Products will appear here
                    </div>
                  )}
              </div>

              <div className="mt-4 flex flex-wrap gap-1">
                {selectedChannels.map((ch) => (
                  <span
                    key={ch}
                    className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-semibold"
                  >
                    {ch === 'WHATSAPP' ? 'WhatsApp' : ch === 'INSTAGRAM' ? 'Instagram' : 'Facebook'}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-4 border-t border-white/30 pt-3">
              <p className="text-[10px] font-semibold opacity-75">Powered by Orio360</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Deals */}
      <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-4">Recent Deals</h2>
      {dealsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : dealRows.length === 0 ? (
        <div className="rounded-xl bg-white dark:bg-neutral-800 shadow-lg p-8 text-center">
          <p className="text-sm text-neutral-500 dark:text-neutral-400">No deals published yet. Create your first deal above.</p>
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Recent deals">
          <DataTable<Row>
            columns={dealColumns}
            data={dealRows}
            loading={isFetching}
            totalCount={dealsData?.meta.total}
            currentPage={dealPage}
            pageSize={20}
            onPageChange={(p) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', String(p));
              setSearchParams(params);
            }}
            stickyHeader
          />
        </section>
      )}
    </div>
  );
}

export function DealOfDayPage() {
  return (
    <PageContainer
      title="Deal of the Day"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Deal of the Day' },
      ]}
    >
      <ErrorBoundary resetKey="comm-deal-of-day">
        <DealOfDayContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
